import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  MdOutlineCelebration,
  MdOutlineCardGiftcard,
  MdOutlineChat,
  MdOutlineChevronLeft,
  MdOutlineChevronRight,
  MdOutlineLaunch,
  MdOutlineLinkOff,
  MdOutlinePointOfSale,
  MdOutlineRefresh,
  MdOutlineSwapHoriz,
} from "react-icons/md";
import axios from "../../config/axiosInstance";
import { EVENT_SIGNUPS, EVENTS } from "../../config/apiConfig";
import useDebouncedValue from "../../hooks/useDebouncedValue";
import { Badge, Card, EmptyState, ErrorBlock } from "../crm/Primitives";
import { SkeletonEventCards } from "../crm/Skeletons";
import {
  formatDate,
  formatMoney,
  formatNumber,
  initialsOf,
  PLAN_TONE,
  whatsappLink,
} from "../crm/crmHelpers";
import Modal from "../modals/Modal";
import ChargePlanModal from "./ChargePlanModal";
import ChangePlanModal from "./ChangePlanModal";
import {
  AREA_LABELS,
  EVENT_STATE_META,
  EVENT_STATE_ORDER,
  formatPhone,
  formatSince,
  giftItemBadge,
  isPointChargeActive,
  isPointChargeSettling,
  labelOf,
  PERIOD_OPTIONS,
  periodRange,
  POINT_STATUS_META,
  SOURCE_LABELS,
  WORK_MODE_LABELS,
} from "./eventHelpers";
import ClientLink from "../crm/ClientLink";

/*
  Cadastros de um evento (ou de todos / dos sem evento): quem se cadastrou pelo
  onboarding, com as respostas, o plano que escolheu e o botão de cobrar.

  `eventId` escolhe o recorte: o id de um evento cadastrado, "none" (passou pelo
  onboarding do evento e ainda não tem evento) ou vazio (todos). Com um evento
  aberto, cada cartão ganha "Tirar do evento".

  O fluxo no balcão é: a pessoa responde o cadastro no celular, aparece aqui, a
  equipe confere o plano (muda se for preciso) e cobra. Confirmar a cobrança
  libera o plano na hora, então o cartão "A cobrar" vira "Pago" sem ninguém
  mexer em mais nada.

  Duas escolhas:

  1. **A lista se atualiza sozinha** (a cada 30 s, só com a aba aberta) e tem o
     botão Atualizar: num evento o cadastro novo chega enquanto a equipe olha
     para a tela.

  2. **Os modais guardam uma cópia do cadastro** de quando foram abertos, em vez
     de reler da lista. Com a lista se atualizando, um cadastro novo empurraria
     o que está sendo cobrado para outra página e o modal sumiria no meio da
     cobrança.

  Os números do topo ignoram os filtros de propósito (o servidor conta a base
  do evento inteira) e são também os botões que filtram por estado.
*/

const PAGE_SIZE = 20;
const REFRESH_MS = 30_000;

const inputClass =
  "w-full min-w-0 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 outline-none focus:border-pink-300 focus:ring-2 focus:ring-pink-100 sm:w-auto";

const FilterTile = ({ label, value, hint, tone, active, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`rounded-2xl border bg-white p-4 text-left shadow-sm transition ${
      active
        ? "border-pink-400 ring-2 ring-pink-100"
        : "border-slate-200 hover:border-pink-200"
    }`}
  >
    <Badge tone={tone}>{label}</Badge>
    <p className="mt-2 text-2xl font-bold leading-none text-slate-900">
      {formatNumber(value)}
    </p>
    {hint ? <p className="mt-1 text-[11px] text-slate-500">{hint}</p> : null}
  </button>
);

const Answer = ({ label, value, empty }) => (
  <div className="min-w-0 rounded-xl bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-100">
    <dt className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400">
      {label}
    </dt>
    <dd
      className={`truncate text-sm ${
        value ? "font-semibold text-slate-800" : "text-slate-400"
      }`}
    >
      {value || empty}
    </dd>
  </div>
);

/*
  Brindes da cliente. Antes do pagamento mostra o que o plano escolhido vai render,
  com aviso quando o estoque não cobre; depois do pagamento, o que ela ganhou. Em
  ambos o selo é o saldo do evento (o servidor calcula; o negativo é o que está
  devendo).
*/
const SignupGifts = ({ gifts }) => {
  const granted = gifts.state === "GRANTED";

  return (
    <div className="rounded-2xl bg-pink-50 p-3 ring-1 ring-inset ring-pink-200">
      <p className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-pink-700">
        <MdOutlineCardGiftcard size={14} />
        {granted ? "Brindes que ganhou" : "Este plano ganha"}
      </p>
      <ul className="mt-1 space-y-1">
        {gifts.items.map((item) => {
          const badge = giftItemBadge(item);

          return (
            <li
              key={item.giftId}
              className="flex flex-wrap items-center justify-between gap-x-2 gap-y-0.5 text-sm text-pink-950"
            >
              <span className="font-bold">
                {item.quantity} × {item.name}
              </span>
              {badge ? <Badge tone={badge.tone}>{badge.label}</Badge> : null}
            </li>
          );
        })}
      </ul>
      {!granted && gifts.items.some((item) => item.short) ? (
        <p className="mt-1 text-[11px] font-semibold text-amber-800">
          O estoque não cobre tudo. Dá para cobrar, mas fica brinde devendo.
        </p>
      ) : null}
    </div>
  );
};

const SignupCard = ({ signup, scope, onCharge, onChangePlan, onUnlink }) => {
  const { company, answers, request, payment, state, event } = signup;
  const meta = EVENT_STATE_META[state];
  const phone = formatPhone(company.phoneNumber);
  const whatsapp = whatsappLink(company.phoneNumber);

  const area = labelOf(AREA_LABELS, answers.area);
  const workMode = labelOf(WORK_MODE_LABELS, answers.workMode);
  const team =
    workMode && answers.teamSize > 1
      ? `${workMode} · ${answers.teamSize} pessoas`
      : workMode;

  const isPaid = state === "PAID";
  const hasPlan = state === "TO_CHARGE" && request;
  // Cobrança na maquininha que ainda não terminou (ou terminou fora do esperado):
  // a equipe precisa ver isso no cartão, sem abrir nada.
  const onMachine = signup.pointCharge;

  return (
    <Card className="p-4">
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="min-w-0 space-y-3">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-pink-50 text-sm font-bold text-pink-700">
              {initialsOf(company.name)}
            </span>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                <ClientLink
                  to={`/clientes/${company.id}`}
                  className="break-words text-base font-bold text-slate-900 hover:text-pink-700"
                >
                  {company.name}
                </ClientLink>
                <span
                  title="Código da cliente"
                  className="rounded-lg bg-slate-900 px-2.5 py-0.5 text-lg font-extrabold leading-tight tracking-tight text-white"
                >
                  #{company.id}
                </span>
                <Badge tone={meta.tone}>{meta.label}</Badge>
              </div>

              <p className="mt-0.5 break-words text-xs text-slate-500">
                {answers.ownerName ? `${answers.ownerName} · ` : ""}
                cadastrou {formatSince(company.createdAt)}
              </p>

              {/* De qual evento veio. Dentro de um evento só interessa COMO
                  entrou; na lista geral, o evento (ou a falta dele). */}
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-500">
                {scope === "event" ? (
                  event ? (
                    <span>
                      Entrou {SOURCE_LABELS[event.source] || "no evento"}
                      {event.linkedByName ? ` (${event.linkedByName})` : ""}
                    </span>
                  ) : null
                ) : event ? (
                  <Link
                    to={`/eventos/${event.id}`}
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    {event.name}
                  </Link>
                ) : (
                  <span className="text-slate-400">Sem evento</span>
                )}
                {scope === "event" && event ? (
                  <button
                    type="button"
                    onClick={() => onUnlink(signup)}
                    className="inline-flex items-center gap-1 font-semibold text-slate-500 underline-offset-2 hover:text-rose-700 hover:underline"
                  >
                    <MdOutlineLinkOff size={14} /> Tirar do evento
                  </button>
                ) : null}
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-600">
            {phone ? (
              whatsapp ? (
                <a
                  href={whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-semibold text-emerald-700 hover:underline"
                >
                  <MdOutlineChat size={14} /> {phone}
                </a>
              ) : (
                <span>{phone}</span>
              )
            ) : null}
            {company.email ? (
              <span className="min-w-0 break-all">{company.email}</span>
            ) : null}
          </div>

          <div>
            <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
              Respostas do cadastro
            </p>
            <dl className="grid gap-2 sm:grid-cols-3">
              <Answer
                label="Área de atuação"
                value={area}
                empty="Não respondeu"
              />
              <Answer label="Como trabalha" value={team} empty="Não respondeu" />
              <Answer label="Cupom" value={company.coupon} empty="Sem cupom" />
            </dl>
          </div>
        </div>

        <div className="flex flex-col gap-3 lg:border-l lg:border-slate-100 lg:pl-4">
          {isPaid ? (
            <>
              <div className="rounded-2xl bg-emerald-50 p-3 ring-1 ring-inset ring-emerald-200">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-emerald-700">
                  Plano liberado
                </p>
                <p className="mt-0.5 text-base font-bold text-emerald-900">
                  {request.planName} · {request.billingCycleLabel}
                </p>
                <p className="mt-1 text-sm text-emerald-800">
                  {payment
                    ? `${payment.label} · ${formatMoney(payment.value)}`
                    : formatMoney(request.amount)}
                </p>
                {request.operator ? (
                  <p className="text-[11px] font-semibold text-emerald-800">
                    Cobrado por {request.operator.name}
                  </p>
                ) : null}
                <p className="text-[11px] text-emerald-700">
                  {payment ? `Pago em ${formatDate(payment.paidAt)}` : null}
                  {company.planExpirationDate
                    ? `${payment ? " · " : ""}vale até ${formatDate(
                        company.planExpirationDate
                      )}`
                    : null}
                </p>
              </div>
              {signup.gifts ? <SignupGifts gifts={signup.gifts} /> : null}
              <ClientLink
                hideWhenBlocked
                to={`/clientes/${company.id}`}
                className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <MdOutlineLaunch size={16} /> Abrir ficha
              </ClientLink>
            </>
          ) : (
            <>
              <div className="rounded-2xl bg-slate-50 p-3 ring-1 ring-inset ring-slate-200">
                <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-400">
                  Plano escolhido
                </p>
                {hasPlan ? (
                  <>
                    <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
                      <span className="text-base font-bold text-slate-900">
                        {request.planName} · {request.billingCycleLabel}
                      </span>
                      <Badge tone={PLAN_TONE[request.planId] || "neutral"}>
                        {request.planId}
                      </Badge>
                    </div>
                    <p className="mt-1 text-2xl font-bold leading-none text-slate-900">
                      {formatMoney(request.amount)}
                    </p>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {request.planId === "DIAMOND"
                        ? "Equipe inteira inclusa"
                        : `${request.numberOfUsers} profissional(is) × ${formatMoney(
                            request.monthlyPrice
                          )} × ${request.months} mês(es)`}
                    </p>
                    {onMachine ? (
                      <p className="mt-2">
                        <Badge
                          tone={
                            isPointChargeSettling(onMachine)
                              ? "info"
                              : POINT_STATUS_META[onMachine.status]?.tone || "neutral"
                          }
                        >
                          {isPointChargeActive(onMachine) ? "Na maquininha: " : ""}
                          {isPointChargeSettling(onMachine)
                            ? "liberando o plano"
                            : (POINT_STATUS_META[onMachine.status]?.label || onMachine.status).toLowerCase()}
                        </Badge>
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p className="mt-0.5 text-sm text-slate-500">
                    {request?.status === "REJECTED"
                      ? "O pedido anterior foi recusado."
                      : "Ainda não escolheu o plano."}
                  </p>
                )}
              </div>

              {hasPlan && signup.gifts ? <SignupGifts gifts={signup.gifts} /> : null}

              <button
                type="button"
                onClick={() => onCharge(signup)}
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-pink-600 px-6 py-3.5 text-base font-bold text-white shadow-md shadow-pink-600/20 transition hover:bg-pink-700 focus:outline-none focus:ring-4 focus:ring-pink-200"
              >
                <MdOutlinePointOfSale size={20} /> Cobrar plano
              </button>
              <button
                type="button"
                onClick={() => onChangePlan(signup)}
                className="inline-flex w-full items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
              >
                <MdOutlineSwapHoriz size={16} />{" "}
                {hasPlan ? "Mudar plano" : "Escolher plano"}
              </button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

const EventSignups = ({ eventId }) => {
  const queryClient = useQueryClient();
  // "event" = dentro de um evento cadastrado; "none" = os sem evento; "all".
  const scope = eventId === "none" ? "none" : eventId ? "event" : "all";

  const [state, setState] = useState("");
  const [term, setTerm] = useState("");
  const [period, setPeriod] = useState("all");
  const [page, setPage] = useState(1);
  // { kind: "change" | "charge", signup, thenCharge? }: a cópia do cadastro de
  // quando o modal foi aberto (ver o comentário do topo).
  const [flow, setFlow] = useState(null);
  const [notice, setNotice] = useState(null);

  const search = useDebouncedValue(term, 400).trim();
  const { from } = useMemo(() => periodRange(period), [period]);

  const query = useQuery(
    ["events", "signups", { eventId: eventId || null, state, search, from, page }],
    async () => {
      const params = new URLSearchParams({
        itemsPerPage: String(PAGE_SIZE),
        page: String(page),
      });
      if (eventId) params.set("eventId", String(eventId));
      if (state) params.set("state", state);
      if (search) params.set("search", search);
      if (from) params.set("from", from);

      const { data } = await axios.get(`${EVENT_SIGNUPS.LIST}?${params}`);
      return data.data;
    },
    {
      keepPreviousData: true,
      refetchInterval: REFRESH_MS,
      refetchIntervalInBackground: false,
    }
  );

  // O aviso de "plano liberado" some sozinho; o que fica é o cartão em Pago.
  useEffect(() => {
    if (!notice) return undefined;

    const timeout = setTimeout(() => setNotice(null), 12_000);
    return () => clearTimeout(timeout);
  }, [notice]);

  const unlink = useMutation(
    ({ signup }) =>
      axios.delete(`${EVENTS.ONE}/${eventId}/clients/${signup.company.id}`),
    {
      onSuccess: (_, { signup }) => {
        setFlow(null);
        setNotice(`${signup.company.name} saiu do evento.`);
        queryClient.invalidateQueries(["events"]);
        queryClient.invalidateQueries(["crm"]);
        queryClient.invalidateQueries(["client", "detail", String(signup.company.id)]);
      },
      onError: (err) =>
        setNotice(
          err.response?.data?.message || "Não foi possível tirar a cliente do evento."
        ),
    }
  );

  const data = query.data;
  const summary = data?.summary;
  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  const change = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const toggleState = (value) =>
    change(setState)(state === value ? "" : value);

  const hasFilters = Boolean(state || search || from);

  const refreshAfterChange = (companyId) => {
    queryClient.invalidateQueries(["events", "signups"]);
    // O pagamento e o plano novo aparecem em outras telas do painel.
    queryClient.invalidateQueries(["client", "detail", String(companyId)]);
    queryClient.invalidateQueries(["crm"]);
    queryClient.invalidateQueries(["companies"]);
    queryClient.invalidateQueries(["payments"]);
  };

  const openCharge = (signup) => {
    // Sem plano escolhido não há o que cobrar: escolhe primeiro e segue.
    if (signup.state === "TO_CHARGE" && signup.request) {
      setFlow({ kind: "charge", signup });
    } else {
      setFlow({ kind: "change", signup, thenCharge: true });
    }
  };

  const openChange = (signup) => setFlow({ kind: "change", signup });

  const openUnlink = (signup) => setFlow({ kind: "unlink", signup });

  const handlePlanSaved = (savedRequest) => {
    const { signup, thenCharge } = flow;
    const updated = {
      ...signup,
      state: "TO_CHARGE",
      request: savedRequest,
    };

    // Sem `await`: o modal seguinte abre já, com o pedido que o servidor
    // devolveu. Esperar a lista deixaria o botão do modal anterior solto e
    // um segundo clique gravaria o plano de novo.
    queryClient.invalidateQueries(["events", "signups"]);

    if (thenCharge) {
      setFlow({ kind: "charge", signup: updated });
      return;
    }

    setFlow(null);
    setNotice(
      `Plano de ${signup.company.name} agora é ${savedRequest.planName} (${savedRequest.billingCycleLabel}), ${formatMoney(
        savedRequest.amount
      )}.`
    );
  };

  const handleCharged = () => {
    const { signup } = flow;

    setFlow(null);
    setNotice(
      `Pagamento registrado: ${signup.request.planName} liberado para ${signup.company.name}.`
    );
    refreshAfterChange(signup.company.id);
  };

  return (
    <div className="space-y-4">
      {notice ? (
        <div
          role="status"
          className="flex items-start justify-between gap-3 rounded-2xl bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-800 ring-1 ring-inset ring-emerald-200"
        >
          <span>{notice}</span>
          <button
            type="button"
            onClick={() => setNotice(null)}
            className="shrink-0 text-xs font-semibold underline"
          >
            Fechar
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <FilterTile
          label="Cadastros"
          tone="info"
          value={summary?.total ?? 0}
          active={!state}
          onClick={() => change(setState)("")}
        />
        {EVENT_STATE_ORDER.map((value) => {
          const key = { TO_CHARGE: "toCharge", NO_PLAN: "noPlan", PAID: "paid" }[
            value
          ];

          return (
            <FilterTile
              key={value}
              label={EVENT_STATE_META[value].label}
              tone={EVENT_STATE_META[value].tone}
              value={summary?.[key] ?? 0}
              hint={
                value === "PAID" && summary
                  ? `${formatMoney(summary.receivedAmount)} recebidos`
                  : null
              }
              active={state === value}
              onClick={() => toggleState(value)}
            />
          );
        })}
      </div>

      <Card className="p-3">
        <div className="grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:items-center">
          <input
            value={term}
            onChange={(event) => change(setTerm)(event.target.value)}
            placeholder="Buscar por nome, e-mail ou telefone"
            aria-label="Buscar cadastro"
            className={`${inputClass} col-span-2 sm:min-w-[240px] sm:flex-1`}
          />
          <select
            value={period}
            onChange={(event) => change(setPeriod)(event.target.value)}
            aria-label="Período do cadastro"
            className={inputClass}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => query.refetch()}
            disabled={query.isFetching}
            className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-60"
          >
            <MdOutlineRefresh
              size={16}
              className={query.isFetching ? "animate-spin" : ""}
            />
            Atualizar
          </button>
        </div>
      </Card>

      {query.isLoading ? (
        <SkeletonEventCards />
      ) : query.error ? (
        <ErrorBlock
          message={
            query.error.response?.data?.message ||
            "Não foi possível carregar os cadastros do evento."
          }
          onRetry={query.refetch}
        />
      ) : data.signups.length ? (
        <div className="space-y-3">
          {data.signups.map((signup) => (
            <SignupCard
              key={signup.company.id}
              signup={signup}
              scope={scope}
              onCharge={openCharge}
              onChangePlan={openChange}
              onUnlink={openUnlink}
            />
          ))}

          {data.total > PAGE_SIZE ? (
            <Card className="flex items-center justify-between gap-3 px-4 py-2.5">
              <span className="min-w-0 text-xs text-slate-500">
                {formatNumber(data.total)} cadastros · página {page} de{" "}
                {totalPages}
              </span>
              <span className="flex shrink-0 gap-1">
                <button
                  type="button"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30 sm:p-1.5"
                  aria-label="Página anterior"
                >
                  <MdOutlineChevronLeft size={20} />
                </button>
                <button
                  type="button"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="rounded-lg p-2 text-slate-500 hover:bg-slate-100 disabled:opacity-30 sm:p-1.5"
                  aria-label="Próxima página"
                >
                  <MdOutlineChevronRight size={20} />
                </button>
              </span>
            </Card>
          ) : null}
        </div>
      ) : (
        <Card>
          <EmptyState
            icon={MdOutlineCelebration}
            title={
              hasFilters
                ? "Nenhum cadastro neste filtro"
                : scope === "none"
                ? "Todo cadastro do evento já tem um evento"
                : scope === "event"
                ? "Nenhuma cliente neste evento ainda"
                : "Ainda não chegou nenhum cadastro do evento"
            }
            description={
              hasFilters
                ? "Tire algum filtro para ver os demais."
                : scope === "event"
                ? "Quem se cadastrar pelo onboarding do evento nas datas dele, ou com o cupom dele, entra sozinha. Para as outras, use Vincular cliente."
                : "Quem preencher o cadastro do evento aparece aqui, com as respostas e o botão de cobrar o plano."
            }
          />
        </Card>
      )}

      {flow?.kind === "change" ? (
        <ChangePlanModal
          signup={flow.signup}
          thenCharge={Boolean(flow.thenCharge)}
          onClose={() => setFlow(null)}
          onSaved={handlePlanSaved}
        />
      ) : null}

      <Modal
        isOpen={flow?.kind === "unlink"}
        onClose={() => (unlink.isLoading ? null : setFlow(null))}
        title="Tirar do evento"
        subtitle={flow?.signup?.company?.name}
        icon={MdOutlineLinkOff}
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setFlow(null)}
              disabled={unlink.isLoading}
              className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => unlink.mutate({ signup: flow.signup })}
              disabled={unlink.isLoading}
              className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-60"
            >
              {unlink.isLoading ? "Tirando..." : "Tirar do evento"}
            </button>
          </div>
        }
      >
        <p className="text-sm text-slate-700">
          Ela deixa de contar nos números deste evento e a saída fica no
          histórico de relacionamento. O vínculo automático não a traz de volta;
          só vinculando à mão.
        </p>
      </Modal>

      {flow?.kind === "charge" ? (
        <ChargePlanModal
          signup={flow.signup}
          onClose={() => setFlow(null)}
          onCharged={handleCharged}
        />
      ) : null}
    </div>
  );
};

export default EventSignups;
